import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Clock, Zap, PhoneOff, VideoOff, FileText, ArrowRight } from "lucide-react";
import api, { eur } from "../api";
import { usePageTitle, TextBlock, MedicalWarning } from "../components/common";
import { TirageFrameNotice } from "../components/TirageFrameNotice";

const GUIDANCE_SUBJECTS = [
  "Lecture générale", "Amour / relation", "Célibat / nouvelle rencontre",
  "Travail / activité professionnelle", "Argent / ressources", "Famille",
  "Amitiés", "Projet / décision", "Chemin personnel", "Autre",
];

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [prep, setPrep] = useState({
    first_name: "", last_name: "", date_of_birth: "", birth_time: "", birth_time_unknown: false,
    birth_city: "", birth_country: "", guidance_subject: "", question_or_intention: "",
    expectations: "", authorization: false, consent: false, frame_ack: false,
  });
  usePageTitle(service?.name);

  useEffect(() => {
    api
      .get(`/services/${slug}`)
      .then((r) => setService(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="container-x py-24 text-center">
        <p className="text-[#7A5D58]">Cette prestation est introuvable.</p>
        <Link to="/guidances" className="btn-secondary mt-6">Retour aux tirages</Link>
      </div>
    );
  if (!service) return <div className="container-x py-24 text-center text-[#7A5D58]">Chargement…</div>;

  const isSos = service.slug === "sos-sorciere";
  const back = service.category === "guidance" ? "/guidances" : "/energetique";
  const isGuidance = service.category === "guidance";
  const setP = (k) => (e) => setPrep({ ...prep, [k]: e.target.value });
  const prepValid = !isGuidance || (
    prep.last_name.trim() && prep.first_name.trim() && prep.date_of_birth &&
    (prep.birth_time_unknown || prep.birth_time) && prep.birth_city.trim() &&
    prep.guidance_subject && prep.question_or_intention.trim() && prep.expectations.trim() &&
    prep.authorization && prep.consent && prep.frame_ack
  );
  const goBuy = () => {
    if (isGuidance) {
      if (!prepValid) return;
      try { sessionStorage.setItem(`prep_${service.slug}`, JSON.stringify(prep)); } catch {}
      navigate(`/commander/${service.slug}`, { state: { prep } });
    } else {
      navigate(`/commander/${service.slug}`);
    }
  };

  return (
    <div className="home-dark min-h-[80vh]">
    <div className="container-x py-10 sm:py-16 fade-up">
      <button
        onClick={() => navigate(back)}
        className="inline-flex items-center gap-2 text-sm text-[#CBB9CF] hover:text-[#F9F5EE] mb-8"
        data-testid="service-back"
      >
        <ArrowLeft size={16} /> Retour
      </button>

      <div className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <div className="flex items-center gap-3 mb-3">
            {service.badge && <span className="badge-gold">{service.badge}</span>}
            {isSos && <span className="badge-soft">Aucun créneau à réserver</span>}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-medium text-[#F9F5EE] flex items-center gap-3">
            {service.name}
            {isSos && <Zap className="text-[#DFC07B]" size={28} />}
          </h1>
          <p className="mt-3 text-lg text-[#CBB9CF]">{service.subtitle}</p>
          <div className="gold-divider my-7 w-28" />
          <TextBlock text={service.description} className="text-base" />

          {service.warning && (
            <div className="mt-6">
              <MedicalWarning tone="light" text={service.warning} />
            </div>
          )}

          {service.category === "guidance" && (
            <div className="mt-8 card-soft p-5" data-testid="service-what-you-get">
              <div className="eyebrow mb-3">Ce que tu reçois ✨</div>
              <ul className="space-y-2 text-sm text-[#7A5D58]">
                {[
                  "un tirage personnalisé",
                  "les messages et ressentis qui ressortent pour toi",
                  "un éclairage sur ta situation",
                  "des pistes de réflexion pour t'aider à avancer",
                  "un compte rendu écrit",
                  isSos ? "une réponse envoyée sous 24 h maximum" : "un compte rendu envoyé sous 5 à 7 jours maximum",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2"><span className="text-[#C9A56A] mt-0.5">✦</span>{t}</li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-[#7A5D58] leading-relaxed">
                Aucun appel. Aucune visio. Tu n'as rien à faire pendant la séance : Noëmie réalise ton tirage de son côté puis t'envoie ton compte rendu par écrit.
              </p>
            </div>
          )}

          {isGuidance && (
            <div className="mt-8 card-soft p-6" data-testid="guidance-form">
              <div className="eyebrow mb-1">Prépare ton tirage</div>
              <p className="text-sm text-[#7A5D58] mb-5">Ces éléments me permettent de me connecter à ton énergie. Ils restent strictement confidentiels.</p>
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className="field-label">Nom</label><input required className="field-input" value={prep.last_name} onChange={setP("last_name")} data-testid="gf-last" /></div>
                  <div><label className="field-label">Prénom</label><input required className="field-input" value={prep.first_name} onChange={setP("first_name")} data-testid="gf-first" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className="field-label">Date de naissance</label><input required type="date" className="field-input" value={prep.date_of_birth} onChange={setP("date_of_birth")} data-testid="gf-dob" /></div>
                  <div>
                    <label className="field-label">Heure de naissance</label>
                    <input type="time" className="field-input" value={prep.birth_time} onChange={setP("birth_time")} disabled={prep.birth_time_unknown} data-testid="gf-time" />
                    <label className="mt-2 flex items-center gap-2 text-xs text-[#7A5D58] cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 accent-[#5A3A4B]" checked={prep.birth_time_unknown} onChange={(e) => setPrep({ ...prep, birth_time_unknown: e.target.checked, birth_time: e.target.checked ? "" : prep.birth_time })} data-testid="gf-time-unknown" />
                      Je ne connais pas mon heure de naissance
                    </label>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className="field-label">Ville de naissance</label><input required className="field-input" value={prep.birth_city} onChange={setP("birth_city")} data-testid="gf-city" /></div>
                  <div><label className="field-label">Pays de naissance <span className="text-[#a58f92]">(facultatif si France)</span></label><input className="field-input" value={prep.birth_country} onChange={setP("birth_country")} placeholder="France" data-testid="gf-country" /></div>
                </div>
                <div>
                  <label className="field-label">Quel sujet souhaites-tu que j'explore pendant ton tirage ?</label>
                  <select required className="field-input" value={prep.guidance_subject} onChange={setP("guidance_subject")} data-testid="gf-subject">
                    <option value="">Choisis un sujet…</option>
                    {GUIDANCE_SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">Question / situation à explorer</label>
                  <p className="text-xs text-[#7A5D58] mb-1.5">Explique-moi la situation, ta question ou ce que tu souhaites que j'explore pendant ton tirage.</p>
                  <textarea required rows={4} className="field-input resize-none" value={prep.question_or_intention} onChange={setP("question_or_intention")} data-testid="gf-question" />
                </div>
                <div>
                  <label className="field-label">Ce que tu attends de ton tirage</label>
                  <p className="text-xs text-[#7A5D58] mb-1.5">Par exemple : comprendre une situation, recevoir un éclairage, identifier un blocage ou obtenir des pistes de réflexion.</p>
                  <textarea required rows={4} className="field-input resize-none" value={prep.expectations} onChange={setP("expectations")} data-testid="gf-expectations" />
                </div>
                <div className="flex gap-2 text-xs text-[#7A5D58] bg-[#C9A56A]/10 rounded-xl p-3">
                  <FileText size={15} className="text-[#C9A56A] shrink-0 mt-0.5" />
                  <span>{isSos ? "Ta réponse te sera envoyée par écrit sous 24 h maximum après ta commande." : "Ton compte rendu sera envoyé par écrit sous 5 à 7 jours maximum après ta commande."}</span>
                </div>
                <div className="space-y-3 rounded-xl border border-[#7A5D58]/15 bg-[#FCFAF7] p-4">
                  <label className="flex items-start gap-3 text-sm text-[#5A3A4B] cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 mt-0.5 accent-[#5A3A4B] shrink-0" checked={prep.authorization} onChange={(e) => setPrep({ ...prep, authorization: e.target.checked })} data-testid="gf-authorization" />
                    <span>Je donne mon accord à Noëmie pour utiliser les informations transmises afin de réaliser mon tirage et se connecter à mon énergie dans le cadre de cette séance.</span>
                  </label>
                  <label className="flex items-start gap-3 text-sm text-[#5A3A4B] cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 mt-0.5 accent-[#5A3A4B] shrink-0" checked={prep.consent} onChange={(e) => setPrep({ ...prep, consent: e.target.checked })} data-testid="gf-consent" />
                    <span>Je comprends que ce tirage est une démarche spirituelle et introspective. Elle ne constitue pas une prédiction certaine de l'avenir et ne remplace aucun avis médical, psychologique, juridique ou financier. Je reste libre de mes choix et de mes décisions.</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {isGuidance && (
            <TirageFrameNotice checked={prep.frame_ack} onChange={(v) => setPrep({ ...prep, frame_ack: v })} />
          )}

          <div className="mt-8 flex flex-wrap gap-4 text-sm text-[#CBB9CF]">
            <span className="inline-flex items-center gap-2"><PhoneOff size={16} /> Aucun appel</span>
            <span className="inline-flex items-center gap-2"><VideoOff size={16} /> Aucune visio</span>
            {service.duration_minutes && (
              <span className="inline-flex items-center gap-2"><Clock size={16} /> {service.duration_minutes} minutes</span>
            )}
            <span className="inline-flex items-center gap-2"><FileText size={16} /> Compte rendu personnel</span>
          </div>
        </div>

        {/* Sticky purchase card */}
        <div className="lg:col-span-2">
          <div className="card-soft p-7 lg:sticky lg:top-28">
            <div className="text-center">
              <div className="eyebrow mb-2">Tarif</div>
              <div className="font-serif text-5xl font-semibold text-[#5A3A4B]">{eur(service.price_cents)}</div>
              {service.duration_minutes && (
                <p className="mt-2 text-sm text-[#7A5D58]">{service.duration_minutes} minutes · séance à distance</p>
              )}
            </div>
            <div className="gold-divider my-6" />
            {service.category === "energetique" && (
  <div className="mb-6 rounded-2xl border border-[#C9A56A]/30 bg-white/70 p-5 text-left">
    <p className="font-serif text-lg text-[#5A3A4B] mb-3">
      Pour préparer ta séance
    </p>
    <p className="text-sm text-[#7A5D58] leading-relaxed">
      Après ton règlement, tu pourras me transmettre directement les informations
  nécessaires à ta séance : ta photo récente, ton intention, ce que tu souhaites
  travailler ainsi que toute information que tu juges utile.
    </p>
    <p className="mt-3 text-xs text-[#8a6a2f] leading-relaxed">
      Ces informations sont transmises de manière privée et servent uniquement à préparer ta séance.
    </p>
  </div>
)}
            <button
              onClick={goBuy}
              disabled={isGuidance && !prepValid}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="service-buy-btn"
            >
              {service.cta || "Réserver"} <ArrowRight size={16} />
            </button>
            {isGuidance && !prepValid && (
              <p className="mt-3 text-xs text-[#8a6a2f] text-center" data-testid="service-buy-hint">
                {prep.frame_ack ? "Remplis le formulaire de tirage pour continuer." : "Remplis le formulaire de tirage et coche la case « J’ai lu et compris le cadre des tirages intuitifs. » pour continuer."}
              </p>
            )}
            <p className="mt-4 text-xs text-[#a58f92] text-center leading-relaxed">
              Paiement sécurisé. À la fin, tu reçois ton compte rendu par WhatsApp ou par e-mail selon ta préférence.
            </p>
            {!isSos && service.booking_required && (
              <p className="mt-3 text-xs text-[#7A5D58] text-center">
                Un créneau sera à réserver après le paiement.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}
